import { AxiosInstance, AxiosResponse } from 'axios';

import { MINUTES_3, MINUTES_5, ONE_SECOND } from './timeConstants';
import { ETLConfigurationPage, ETLEvent, IntegrationRoles, IntegrationTeams } from '../api/types';
import { ConfigurationType } from '../scene/integrations/types';

export const WEBEXCC_SCOPES = {
  config: 'cjp:config',
  configWrite: 'cjp:config_write',
  configRead: 'cjp:config_read',
  ccaiRead: 'cjp-ccai:read',
  ccaiWrite: 'cjp-ccai:write',
  ccaiConfigRead: 'cjp-ccai:config_read',
} as const;

export const WEBEX_CALLING_SCOPES = {
  peopleRead: 'spark-admin:people_read',
  devicesRead: 'spark-admin:devices_read',
  groupsRead: 'identity:groups_read',
  licensesRead: 'spark-admin:licenses_read',
} as const;

export type MultiTenantEnv = {
  tenancy: 'multi';
  wxccRootUrl: string;
  wxccApiVersion: string;
  wxccAuthorizePath: string;
  wxccTokensPath: string;
  webexccApiUrl: string;
  msteamsApiUrl: string;
  zoomccApiUrl: string;
  webexCallingApiUrl: string;
};

export type SingleTenantEnv = {
  tenancy: 'single';
  ssoFrontendUrl: string;
  tenant: string;
  wxccRootUrl: string;
  wxccApiVersion: string;
  wxccAuthorizePath: string;
  wxccTokensPath: string;
  webexccApiUrl: string;
  msteamsApiUrl: string;
  zoomccApiUrl: string;
  webexCallingApiUrl: string;
};

export interface RuntimeConfiguration {
  env: MultiTenantEnv | SingleTenantEnv;
}

export interface IntegrationRequestBodyType {
  clientId: string;
  clientSecret: string;
  organizationId: string;
  apiUrl: string;
}

export interface WebexCallingTeamsRequestBodyType {
  accessToken: string;
  apiUrl: string;
}

/**
 * Select first part of subdomain, which is representing keycloak's realm
 * @example: tenant1.some.subdomain.myeleveo.com => tenant1
 */
export const FIRST_PART_OF_SUBDOMAIN = /\.(.+)/;

export const CLIENT_ID = 'etl-ui';
export const ETL_MANAGER_ID = 'etl-manager-app';

export const QUALITY_MANAGEMENT_PATH = '/app';
export const WORKFORCE_MANAGEMENT_PATH = '/wfm';

export const AUTH_PATH = '/auth';
export const ETL_MANAGEMENT_UI = '/etl';
export const USER_PROFILE_PATH = '/app/um/user-profile';
export const USER_MANAGEMENT_PATH = '/app/um/users';

export const REALM_PLACEHOLDER = '{realm}';

/**
 * DEFAULT CONFIGURATION
 */

const configuredNotificationDuration = Number.parseInt(<string>process.env.REACT_APP_NOTIFICATION_DURATION || '', 10);
const configuredLoginTimeout = Number.parseInt(<string>process.env.REACT_APP_WEBEXCC_LOGIN_TIMEOUT || '', 10);

export const defaultConfiguration = {
  appUrl: `${window.location.origin}${process.env.PUBLIC_URL}`,
  authUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${AUTH_PATH}`,
  basePath: process.env.PUBLIC_URL,
  clientId: CLIENT_ID,
  qualityManagementUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${QUALITY_MANAGEMENT_PATH}`,
  workforceManagementUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${WORKFORCE_MANAGEMENT_PATH}`,
  tenant: `${process.env.REACT_APP_TENANT}`,
  userManagementUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${USER_MANAGEMENT_PATH}`,
  userProfileUrl: `${process.env.REACT_APP_PROXY_SERVICE_TARGET_PROTOCOL}${process.env.REACT_APP_TENANT}.${process.env.REACT_APP_PROXY_SERVICE_TARGET_HOST}${USER_PROFILE_PATH}`,
  notificationDuration: Number.isNaN(configuredNotificationDuration)
    ? MINUTES_3
    : configuredNotificationDuration * ONE_SECOND,
  webexParamsUrl: `${process.env.REACT_APP_WEBEXCC_ROOT_URL}${process.env.REACT_APP_WEBEXCC_API_VERSION}${process.env.REACT_APP_WEBEXCC_AUTHORIZE_PATH}`,
  webexTokensUrl: `${process.env.REACT_APP_WEBEXCC_ROOT_URL}${process.env.REACT_APP_WEBEXCC_API_VERSION}${process.env.REACT_APP_WEBEXCC_TOKENS_PATH}`,
  webexTimeout: Number.isNaN(configuredLoginTimeout) ? MINUTES_5 : configuredLoginTimeout * ONE_SECOND,
  webexCCScopes: [WEBEXCC_SCOPES.config, WEBEXCC_SCOPES.configRead, WEBEXCC_SCOPES.configWrite].join(' '),
  webexCallingScopes: [
    WEBEX_CALLING_SCOPES.peopleRead,
    WEBEX_CALLING_SCOPES.devicesRead,
    WEBEX_CALLING_SCOPES.groupsRead,
    WEBEX_CALLING_SCOPES.licensesRead,
  ].join(' '),

  webexccApiUrl: process.env.REACT_APP_WXCC_API_ROOT_URL ?? 'https://api.wxcc-us1.cisco.com',
  msteamsApiUrl: process.env.REACT_APP_MSTEAMS_API_ROOT_URL ?? 'https://graph.microsoft.com',
  zoomccApiUrl: process.env.REACT_APP_ZOOMCC_API_ROOT_URL ?? 'https://api.zoom.us/v2/contact_center',
  webexCallingApiUrl: process.env.REACT_APP_WXCALLING_API_ROOT_URL ?? 'https://webexapis.com/v1',
};

/**
 * Other products URLs
 */
export const getTenant = (env: RuntimeConfiguration['env']) => {
  const { host } = window.location;
  return env?.tenancy === 'multi' ? host.split(FIRST_PART_OF_SUBDOMAIN) : [env?.tenant ?? 'error:tenantIsMissing'];
};

export const getAuthUrl = (env: RuntimeConfiguration['env']) => {
  const { origin } = window.location;
  return env?.tenancy === 'single' ? env.ssoFrontendUrl : `${origin}${AUTH_PATH}`;
};

export const getQualityManagementUrl = () => {
  const { origin } = window.location;
  return `${origin}${QUALITY_MANAGEMENT_PATH}`;
};

export const getWorkforceManagementUrl = () => {
  const { origin } = window.location;
  return `${origin}${WORKFORCE_MANAGEMENT_PATH}`;
};

export const getUserManagementUrl = () => {
  const { origin } = window.location;
  return `${origin}${USER_MANAGEMENT_PATH}`;
};

export const getUserProfileUrl = () => {
  const { origin } = window.location;
  return `${origin}${USER_PROFILE_PATH}`;
};

/**
 * ENDPOINTS
 */

export const ENDPOINTS = {
  events: '/etl-manager/event',
  configurations: '/etl-manager/configuration',
  configurationsOverview: '/etl-manager/configuration-overview',
  eleveoUserRoles: '/etl-manager/roles/keycloak',
  webexCCUserRoles: '/etl-manager/roles/webexcc',
  zoomCCUserRoles: '/etl-manager/roles/zoomcc',
  msTeamsUserRoles: '/etl-manager/roles/msteams',
  webexCallingTeams: '/etl-manager/groups/webexcalling',
  appSwitcherApps: `/auth/realms/${REALM_PLACEHOLDER}/users/applications/app-switcher`,
};

export class EndpointsFactory {
  static getEvents(client: AxiosInstance, fromDateTime: string) {
    return client.get<ETLEvent[]>(ENDPOINTS.events, { params: { fromDateTime } }).then((response) => response.data);
  }

  static getConfigurations(client: AxiosInstance, pageSize: number, page: number, integrationTypes: string) {
    return client
      .get<ETLConfigurationPage, AxiosResponse<ETLConfigurationPage>>(ENDPOINTS.configurationsOverview, {
        params: {
          pageSize,
          page,
          integrations: integrationTypes
            .split(',')
            .map((i) => i.toUpperCase())
            .join(','),
        },
      })
      .then((response) => response.data);
  }

  static getConfigurationById(client: AxiosInstance, id: number) {
    return client
      .get<ConfigurationType, AxiosResponse<ConfigurationType>>(`${ENDPOINTS.configurations}/${id}`)
      .then((response) => response.data);
  }

  static createConfiguration(client: AxiosInstance, configuration: ConfigurationType) {
    return client
      .post<ConfigurationType, AxiosResponse<ConfigurationType>>(ENDPOINTS.configurations, configuration)
      .then((response) => response.data);
  }

  static updateConfiguration(client: AxiosInstance, configuration: ConfigurationType) {
    return client
      .put<ConfigurationType, AxiosResponse<ConfigurationType>>(ENDPOINTS.configurations, configuration)
      .then((response) => response.data);
  }

  static deleteConfigurationById(client: AxiosInstance, id: number) {
    return client
      .delete<ConfigurationType, AxiosResponse<ConfigurationType>>(`${ENDPOINTS.configurations}/${id}`)
      .then((response) => response.data);
  }

  static getEleveoUserRoles(client: AxiosInstance) {
    return client
      .get<IntegrationRoles, AxiosResponse<IntegrationRoles>>(ENDPOINTS.eleveoUserRoles)
      .then((response) => response.data.roles);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- data is defined so all endpoints for integration roles have same API
  static getWebexCCUserRoles(client: AxiosInstance, data: IntegrationRequestBodyType) {
    return client
      .get<IntegrationRoles, AxiosResponse<IntegrationRoles>>(ENDPOINTS.webexCCUserRoles)
      .then((response) => response.data.roles);
  }

  static getMSTeamsUserRoles(client: AxiosInstance, data: IntegrationRequestBodyType) {
    return client
      .post<IntegrationRoles, AxiosResponse<IntegrationRoles>>(ENDPOINTS.msTeamsUserRoles, data)
      .then((response) => response.data.roles);
  }

  static getZoomCCUserRoles(client: AxiosInstance, data: IntegrationRequestBodyType) {
    return client
      .post<IntegrationRoles, AxiosResponse<IntegrationRoles>>(ENDPOINTS.zoomCCUserRoles, data)
      .then((response) => response.data.roles);
  }

  static getAppSwitcherApps(client: AxiosInstance, realm: string) {
    return client
      .get<string[], AxiosResponse<string[]>>(ENDPOINTS.appSwitcherApps.replace(REALM_PLACEHOLDER, realm))
      .then((response) => response.data);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getWebexCallingTeams(client: AxiosInstance, data: WebexCallingTeamsRequestBodyType) {
    return client
      .post<IntegrationTeams, AxiosResponse<IntegrationTeams>>(ENDPOINTS.webexCallingTeams, data)
      .then((response) => response.data.groups);
  }
}

/**
 * WebexCC
 */

export const getWebexTokensUrl = (env: RuntimeConfiguration['env'], locallyDefinedUrl: string): string => {
  if (!env?.wxccRootUrl || !env?.wxccApiVersion || !env?.wxccTokensPath) {
    return locallyDefinedUrl;
  }
  return `${env?.wxccRootUrl}${env?.wxccApiVersion}${env?.wxccTokensPath}`;
};

export const getWebexParamsUrl = (env: RuntimeConfiguration['env'], locallyDefinedUrl: string): string => {
  if (!env?.wxccRootUrl || !env?.wxccApiVersion || !env?.wxccAuthorizePath) {
    return locallyDefinedUrl;
  }
  return `${env?.wxccRootUrl}${env?.wxccApiVersion}${env?.wxccAuthorizePath}`;
};

/**
 * APPLICATIONS
 */

export const APPLICATIONS = {
  qm: 'QM',
  wfm: 'WFM',
  um: 'User Management',
  etl: 'ETL Management',
} as const;
